import {Button, Col, FloatingLabel, Form, InputGroup, Offcanvas, Row, Spinner} from 'react-bootstrap'
import {FormattedMessage, defineMessages, useIntl} from 'react-intl'
import {useEffect, useMemo, useState} from 'react'
import {useMutation} from '@apollo/client'
import {toast} from 'react-toastify'
import {SEND_MAIL} from '../../../../gql/mutations/integrationsMutations'
import {LeadByID} from '../../leadTypes'
import {useGetUser} from '../../../../store/userStore'

interface SendEmailResponse {
  sendEmail: {success: boolean; errorMessage?: string | null}
}

const requiredMark = '*'

const mM = defineMessages({
  title: {id: 'leads.nav.sendEmail', defaultMessage: 'Send email'},
  from: {id: 'common.field.from', defaultMessage: 'From'},
  to: {id: 'common.field.to', defaultMessage: 'To'},
  subject: {id: 'common.field.subject', defaultMessage: 'Subject'},
  cc: {id: 'leads.mail.cc', defaultMessage: 'Cc'},
  bcc: {id: 'leads.mail.bcc', defaultMessage: 'Bcc'},
  message: {id: 'common.field.message', defaultMessage: 'Message'},
  toastComplete: {id: 'leads.mail.toast.complete', defaultMessage: 'Please complete all required fields.'},
  toastSent: {id: 'leads.mail.toast.sent', defaultMessage: 'Email sent successfully.'},
  errUnable: {id: 'leads.mail.error.unable', defaultMessage: 'Unable to send email'},
})

export const FormSendMail: React.FC<{onBack: () => void; lead: LeadByID}> = ({onBack, lead}) => {
  const intl = useIntl()
  const errMsg = (message?: string) =>
    intl.formatMessage({id: 'common.error.withMessage', defaultMessage: 'Error: {message}'}, {message})
  const [sendEmail, {loading}] = useMutation<SendEmailResponse>(SEND_MAIL)
  const user = useGetUser((s) => s.user?.user)
  const userName = useMemo(() => {
    const fullName = [user?.firstName, user?.lastName].filter(Boolean).join(' ').trim()
    return fullName || user?.email || 'N/R'
  }, [user?.firstName, user?.lastName, user?.email])

  const [toEmail, setToEmail] = useState(lead.email || '')
  const [subject, setSubject] = useState('')
  const [message, setMessage] = useState('')

  useEffect(() => {
    const signature = `\n\n\n\n${userName}\nThe Future Insurance\nCopyrights © All rights reserved.`
    setMessage(signature)
  }, [userName])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!toEmail || !subject || !message) {
      toast.error(intl.formatMessage(mM.toastComplete))
      return
    }
    try {
      const {data} = await sendEmail({variables: {toEmail, subject, message}})
      if (data?.sendEmail?.success) {
        toast.success(intl.formatMessage(mM.toastSent))
        onBack()
        return
      }
      toast.error(errMsg(data?.sendEmail?.errorMessage || intl.formatMessage(mM.errUnable)))
    } catch (err) {
      toast.error(errMsg(String(err)))
    }
  }
  return (
    <>
      <Offcanvas.Header closeButton className='bg-light'>
        <div className='d-flex align-items-center justify-content-between w-100'>
          <Offcanvas.Title className='d-flex align-items-center gap-4'>{intl.formatMessage(mM.title)}</Offcanvas.Title>
        </div>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <form onSubmit={handleSubmit}>
          <div className='w-100 h-100'>
            <div>
              <Row>
                <Col className='col-12 mb-4'>
                  <FloatingLabel controlId='from' label={intl.formatMessage(mM.from)} className='mb-3'>
                    <Form.Control placeholder={intl.formatMessage(mM.from)} name='from' value={user?.email || ''} disabled />
                  </FloatingLabel>
                </Col>
                <Col className='col-12 mb-4'>
                  <FloatingLabel
                    controlId='to'
                    label={
                      <span>
                        <FormattedMessage id='common.field.to' defaultMessage='To' /> <span style={{color: 'red'}}>{requiredMark}</span>
                      </span>
                    }
                    className='mb-3'
                  >
                    <Form.Control
                      placeholder={intl.formatMessage(mM.to)}
                      name='to'
                      value={toEmail}
                      onChange={(e) => setToEmail(e.target.value)}
                    />
                  </FloatingLabel>
                </Col>
                <Col className='col-12 mb-4'>
                  <InputGroup className='mb-3'>
                    <FloatingLabel
                      controlId='subject'
                      label={
                        <span>
                          <FormattedMessage id='common.field.subject' defaultMessage='Subject' /> <span style={{color: 'red'}}>{requiredMark}</span>
                        </span>
                      }
                    >
                      <Form.Control
                        name='subject'
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                      />
                    </FloatingLabel>
                    <InputGroup.Text id='basic-addon2'>{intl.formatMessage(mM.cc)}</InputGroup.Text>
                    <InputGroup.Text id='basic-addon3'>{intl.formatMessage(mM.bcc)}</InputGroup.Text>
                  </InputGroup>
                </Col>
                <Col className='col-12 mb-4'>
                  <FloatingLabel
                    controlId='message'
                    label={
                      <span>
                        <FormattedMessage id='common.field.message' defaultMessage='Message' /> <span style={{color: 'red'}}>{requiredMark}</span>
                      </span>
                    }
                    className='mb-3'
                  >
                    <Form.Control
                      placeholder={intl.formatMessage(mM.message)}
                      as='textarea'
                      rows={12}
                      type='textarea'
                      name='message'
                      className='h-100'
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                    />
                  </FloatingLabel>
                </Col>
              </Row>
            </div>
            <div className='d-flex algin-items-center justify-content-end gap-3 mt-4'>
              <Button type='button' variant='light' onClick={onBack}>
                <FormattedMessage id='common.action.cancel' defaultMessage='Cancel' />
              </Button>
              <Button type='submit' variant='primary' disabled={loading || !toEmail || !subject}>
                {loading ? <Spinner animation='border' size='sm' /> : intl.formatMessage({id: 'common.action.send', defaultMessage: 'Send'})}
              </Button>
            </div>
          </div>
        </form>
      </Offcanvas.Body>
    </>
  )
}
